import {
  Title, Select, Stack, Button, Text, Group, type StackProps, type ComboboxData
} from '@mantine/core';
import { useCallback, useState, useMemo } from 'react';
import { formatDistance } from 'date-fns';

import useEnsisQuery from '../../hooks/useEnsisQuery';
import useEnsisMutation from '../../hooks/useEnsisMutation';
import { hasPermissionsForObject } from '../../utils/apiUtils';

import { CenteredLoader } from '../../components';
import type { Organization } from '../../types/apiTypes';

const getOrgStatusLabel = (org: Organization): string => {
  const {
    access_revoked: accessRevoked,
    access_revoked_at: accessRevokedAt
  } = org;
  if (accessRevoked) {
    return 'Revoked';
  } else if (
    accessRevokedAt !== undefined && accessRevokedAt != null
  ) {
    const expiresIn = formatDistance(new Date(accessRevokedAt), Date());
    return `${expiresIn} Remaining`;
  }

  return 'Active';
};

const RevokeOrganizationAccess: React.FC<StackProps> = (props: StackProps) => {
  const [selectedOrgSlug, setSelectedOrgSlug] = useState<string | null>(null);
  const { data: orgData, isLoading: queryLoading } = useEnsisQuery('/app/organizations');

  const orgWithRevokePerms = orgData?.items?.filter((org) => (
    hasPermissionsForObject(org, 'revoke_access')
  ));

  const revokeAccessMutation = useEnsisMutation(
    `/app/organizations/${selectedOrgSlug}/revoke-access`,
    {
      requestType: 'post',
      successMessage: 'Organization access revoked',
      queryKeysToInvalidate: ['/app/organizations'],
      onSuccess: () => { setSelectedOrgSlug(null); }
    }
  );

  const restoreAccessMutation = useEnsisMutation(
    `/app/organizations/${selectedOrgSlug}/restore-access`,
    {
      requestType: 'post',
      successMessage: 'Organization access restored',
      queryKeysToInvalidate: ['/app/organizations'],
      onSuccess: () => { setSelectedOrgSlug(null); }
    }
  );

  const handleRevokeAccess = useCallback(() => {
    if (selectedOrgSlug != null) {
      revokeAccessMutation.mutate({ organization_slug: selectedOrgSlug });
    }
  }, [selectedOrgSlug]);

  const handleRestoreAccess = useCallback(() => {
    if (selectedOrgSlug != null) {
      restoreAccessMutation.mutate({ organization_slug: selectedOrgSlug });
    }
  }, [selectedOrgSlug]);

  const loading = queryLoading || revokeAccessMutation.isPending;

  const orgItems: ComboboxData = useMemo(() => (
    orgWithRevokePerms?.map((org) => ({
      label: `${org.name}${org.access_revoked ? ' (REVOKED)' : ''}`,
      value: org.slug
    })) ?? []
  ), [orgWithRevokePerms]);

  const selectedOrg = useMemo(() => (
    orgWithRevokePerms?.find(
      (org) => org.slug === selectedOrgSlug
    )
  ), [selectedOrgSlug]);

  const selectedOrgHasAccessRevoked = selectedOrg?.access_revoked ?? false;

  if (loading) {
    return <CenteredLoader />;
  }

  return (
    <Stack maw={500} {...props}>
      <Title order={6}>Revoke / Restore Organization Access</Title>
      <Select
        label='Organization'
        placeholder='Select organization'
        data={orgItems}
        onChange={setSelectedOrgSlug}
        value={selectedOrgSlug}
      />
      <Group gap={8}>
        <Text fz='sm' fw='bold'>Access Status:</Text>
        { selectedOrg !== undefined && <Text fz='sm'>{getOrgStatusLabel(selectedOrg)}</Text> }
      </Group>
      <Button
        onClick={selectedOrgHasAccessRevoked ? handleRestoreAccess : handleRevokeAccess}
        disabled={selectedOrgSlug == null}
      >
        {selectedOrgHasAccessRevoked ? 'Restore Access' : 'Revoke Access'}
      </Button>
    </Stack>
  );
};

export default RevokeOrganizationAccess;
